/**
 * Project-wide search (F3, like Blender's menu search and Unity's Search
 * window): providers each answer a query for one kind of thing — commands,
 * assets, scene entities, components, settings — and the Search window merges
 * and ranks the results. A prefix narrows to one provider (`a:` assets,
 * `e:` entities, `>` commands, ...).
 *
 *   registerSearchProvider({ id: 'prefabs', label: 'Prefabs', prefix: 'p:',
 *     search: (q) => prefabs.filter(...).map(...) });
 */

export interface SearchItem {
  id: string;
  label: string;
  /** Secondary text (a path, the entity it is on). */
  detail?: string;
  icon?: string;
  /** Right-aligned hint, usually a key binding. */
  hint?: string;
  /** Set for command results: lets the row be starred into Quick Favorites. */
  commandId?: string;
  run: () => void | Promise<void>;
  /** Relevance from `matchScore`; higher sorts first. */
  score: number;
  /** More things to do with the result (right-click), like Unity Search item actions. */
  actions?: SearchAction[];
}

export interface SearchAction {
  id: string;
  label: string;
  icon?: string;
  run: () => void | Promise<void>;
}

export interface SearchProvider {
  id: string;
  label: string;
  icon?: string;
  /** Typing this prefix searches only this provider (`a:`). */
  prefix?: string;
  /** Shown with an empty query too (commands are; files are not). */
  showEmpty?: boolean;
  /** Only searched when chosen by its prefix or chip (Unity's explicit providers). */
  explicit?: boolean;
  /** In "All", shown only while the box is empty (Recent); its prefix still searches it. */
  emptyOnly?: boolean;
  /** Higher providers win ties. */
  priority?: number;
  search(query: string): SearchItem[] | Promise<SearchItem[]>;
}

const providers = new Map<string, SearchProvider>();
const listeners = new Set<() => void>();

export function registerSearchProvider(provider: SearchProvider): () => void {
  providers.set(provider.id, provider);
  listeners.forEach((l) => l());
  return () => {
    if (providers.get(provider.id) === provider) providers.delete(provider.id);
    listeners.forEach((l) => l());
  };
}

export function searchProviders(): SearchProvider[] {
  return [...providers.values()].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
}

export function onSearchProvidersChanged(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Relevance of `text` for `query`, or null when it does not match.
 * Every query word must appear; whole-prefix and word-start matches rank
 * above inner substrings, which rank above initials ("gts" for "Go to Sound").
 */
export function matchScore(query: string, text: string): number | null {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  const t = text.toLowerCase();
  let score = 0;
  for (const word of q.split(/\s+/)) {
    const at = t.indexOf(word);
    if (at === 0) score += 30;
    else if (at > 0 && /[\s:/._\-·›>]/.test(t[at - 1]!)) score += 20;
    else if (at > 0) score += 10;
    else if (word.length > 1 && isSubsequence(word, initials(t))) score += 6;
    else return null;
  }
  // Shorter texts are closer matches.
  return score - Math.min(10, t.length / 20);
}

function initials(text: string): string {
  return text
    .split(/[\s:/._\-·›>]+/)
    .map((w) => w[0] ?? '')
    .join('');
}

function isSubsequence(needle: string, hay: string): boolean {
  let i = 0;
  for (const ch of hay) if (ch === needle[i] && ++i === needle.length) return true;
  return needle.length === 0;
}

/** Split `a: foo` into the provider with that prefix and the rest of the query. */
export function parseQuery(query: string): { provider: SearchProvider | null; text: string } {
  const trimmed = query.trimStart();
  for (const provider of providers.values()) {
    if (provider.prefix && trimmed.toLowerCase().startsWith(provider.prefix)) {
      return { provider, text: trimmed.slice(provider.prefix.length).trim() };
    }
  }
  return { provider: null, text: query.trim() };
}

/** Score for providers: the label, or label plus extra text (slightly lower), or null. */
export function scoreOf(query: string, label: string, extra = ''): number | null {
  const a = matchScore(query, label);
  const b = extra ? matchScore(query, `${label} ${extra}`) : null;
  if (a === null && b === null) return null;
  return Math.max(a ?? -Infinity, (b ?? -Infinity) - 5);
}
